import { createContext, useContext, useEffect, useState } from 'react'

import { LOTOS_LOGO_DARK, LOTOS_LOGO_LIGHT } from '../lib/branding.js'

const STORAGE_KEY = 'lotos-theme'
const COOKIE_NAME = 'lotos_theme'

const ThemeContext = createContext(null)

/**
 * Снимаем host-only `lotos_theme` на текущем поддомене, чтобы не оставалось
 * дубликатов: иначе `document.cookie` отдаёт два раза `lotos_theme=…`, и regex
 * мог брать устаревший `dark` с dex вместо общего `light` с cex.
 */
function clearHostOnlyThemeCookie() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const h = window.location.hostname
  if (!h.endsWith('lotosterminal.pro')) return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
}

function readThemeFromCookie() {
  if (typeof document === 'undefined') return null
  const raw = document.cookie
  if (!raw) return null
  const parts = raw.split(';').map((s) => s.trim())
  const out = []
  for (const p of parts) {
    const m = p.match(/^lotos_theme=(.+)$/i)
    if (m) {
      const v = String(m[1] || '')
        .trim()
        .toLowerCase()
      if (v === 'dark' || v === 'light') out.push(v)
    }
  }
  if (out.length === 0) return null
  if (out.length === 1) return out[0]
  if (out.includes('light') && out.includes('dark')) return 'light'
  return out[out.length - 1]
}

function writeThemeCookie(theme) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const t = theme === 'dark' ? 'dark' : 'light'
  const h = window.location.hostname
  if (!h.endsWith('lotosterminal.pro')) return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  clearHostOnlyThemeCookie()
  document.cookie = `${COOKIE_NAME}=${t}; Path=/; Domain=.lotosterminal.pro; Max-Age=31536000; SameSite=Lax${secure}`
}

/**
 * Раз localStorage не общий для dex.* и cex.*, на проде читаем/пишем cookie на
 * `.lotosterminal.pro`, чтобы тема совпадала при смене поддомена.
 */
export function readThemeAtBoot() {
  if (typeof window === 'undefined') return 'light'
  try {
    const h = window.location.hostname
    if (h.endsWith('lotosterminal.pro')) {
      clearHostOnlyThemeCookie()
      const c = readThemeFromCookie()
      if (c) {
        try {
          if (localStorage.getItem(STORAGE_KEY) !== c) {
            localStorage.setItem(STORAGE_KEY, c)
          }
        } catch (_) {}
        return c
      }
    }
    const fromLs = localStorage.getItem(STORAGE_KEY) || 'light'
    const normalized = fromLs === 'dark' ? 'dark' : 'light'
    if (h.endsWith('lotosterminal.pro')) {
      writeThemeCookie(normalized)
    }
    return normalized
  } catch {
    return 'light'
  }
}

/** Run before React render so dark class + favicon match CSP (no inline script in index.html). */
export function bootstrapLotosTheme() {
  if (typeof window === 'undefined') return
  try {
    const theme = readThemeAtBoot()
    const isDark = theme === 'dark'
    const root = document.documentElement
    if (isDark) root.classList.add('dark')
    else root.classList.remove('dark')
    const lightIcon = LOTOS_LOGO_LIGHT
    const darkIcon = LOTOS_LOGO_DARK
    const link = document.querySelector('link[rel="icon"]')
    if (link) link.href = isDark ? darkIcon : lightIcon
    const apple = document.querySelector('link[rel="apple-touch-icon"]')
    if (apple) apple.href = isDark ? darkIcon : lightIcon
  } catch (_) {}
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readThemeAtBoot())

  useEffect(() => {
    if (typeof window === 'undefined') return
    const h = window.location.hostname
    if (!h.endsWith('lotosterminal.pro')) return
    const sync = () => {
      clearHostOnlyThemeCookie()
      const next = readThemeAtBoot()
      setThemeState(next)
      bootstrapLotosTheme()
    }
    window.addEventListener('focus', sync)
    const onVis = () => {
      if (document.visibilityState === 'visible') sync()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch (_) {}
    writeThemeCookie(theme)
    const lightIcon = LOTOS_LOGO_LIGHT
    const darkIcon = LOTOS_LOGO_DARK
    const favicon = document.querySelector('link[rel="icon"]')
    if (favicon) favicon.href = theme === 'dark' ? darkIcon : lightIcon
    const apple = document.querySelector('link[rel="apple-touch-icon"]')
    if (apple) apple.href = theme === 'dark' ? darkIcon : lightIcon
  }, [theme])

  const setTheme = (value) => {
    setThemeState(value === 'dark' ? 'dark' : 'light')
  }

  const toggleTheme = () => {
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'))
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
